import { useMemo, useState } from "react"
import {
	IconChartActivityOutline18 as HealthIcon,
	IconCircleUserOutline18 as LeadIcon,
	IconUserXmarkOutline18 as UserXmarkIcon,
} from "nucleo-ui-outline-18"

import type { LinearUser, Project } from "@productlane/zero"
import type { UpdateProjectArgs } from "@productlane/zero/types"
import { cn } from "@productlane/lib/src/cn"
import { Avatar, CircularProgress, LinearIcon } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { PopoverButton } from "@/components/popover-button"
import {
	propertyIconClassName,
	PropertyTrigger,
} from "@/components/property-trigger"
import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { useRole } from "@/lib/useRole"
import { mutateOk, reportServerRejection, useZero } from "@/lib/zero"
import { IssuePriorityIcon } from "@/screens/app/issues/issue-priority-icon"
import { progressPercent, PROJECT_HEALTH } from "./project-health"

export type ProjectWithLinearRelations = Project & {
	lead?: LinearUser | undefined
}

const OPTION_QUERY = { cacheTime: Infinity, staleTime: Infinity } as const

export const ProjectLinearProperties = ({
	project,
}: {
	project: ProjectWithLinearRelations
}) => {
	const { role } = useRole()
	const zero = useZero()
	const [isLeadOpen, setIsLeadOpen] = useState(false)
	const [isPriorityOpen, setIsPriorityOpen] = useState(false)

	// A lead can come from any of the project's teams; the first one covers most projects.
	const teamId = project.linearTeamIds?.[0]
	const { data: members } = api.linear.members.useQuery(
		{ teamId: teamId ?? "" },
		{
			...OPTION_QUERY,
			enabled: !!teamId && (isLeadOpen || (!!project.leadId && !project.lead)),
		},
	)
	const { data: priorities } = api.linear.priorities.useQuery(undefined, {
		...OPTION_QUERY,
		enabled: isPriorityOpen,
	})

	const save = async (update: Omit<UpdateProjectArgs, "id">, event: string) => {
		const reportFailure = (message?: string) =>
			toast.error({
				title: "Linear did not save this change",
				description: message,
				icon: LinearIcon,
			})
		try {
			await mutateOk(
				reportServerRejection(
					zero.mutate(mutators.projects.update({ id: project.id, ...update })),
					reportFailure,
				),
			)
			analytics.track(event)
		} catch (error) {
			reportFailure(error instanceof Error ? error.message : undefined)
		}
	}

	const leadOptions = useMemo(
		() =>
			(members ?? [])
				.filter(({ app }) => !app)
				.map(({ id, name, avatarUrl }) => ({
					value: id,
					label: (
						<div className="flex items-center gap-2">
							<Avatar name={name} image={avatarUrl} size="xs" />
							<span className="truncate">{name}</span>
						</div>
					),
				})),
		[members],
	)

	const priorityOptions = useMemo(
		() =>
			(priorities ?? []).map(({ label, priority }) => ({
				value: String(priority),
				label: (
					<div className="flex items-center gap-2">
						<IssuePriorityIcon
							priority={label}
							className="h-3.5 w-3.5 flex-none text-secondary"
						/>
						<span className="truncate">{label}</span>
					</div>
				),
			})),
		[priorities],
	)

	const lead = project.lead
		? { name: project.lead.name ?? "", avatarUrl: project.lead.avatarUrl }
		: members?.find(({ id }) => id === project.leadId)
	const health = project.health ? PROJECT_HEALTH[project.health] : undefined
	const percent = progressPercent(project.progress)

	const leadTrigger = (
		<PropertyTrigger
			tooltip="Change lead"
			icon={
				lead ? (
					<Avatar name={lead.name} image={lead.avatarUrl} size="xs" />
				) : (
					<LeadIcon className={propertyIconClassName} />
				)
			}
		>
			{project.leadId ? (lead?.name ?? "Lead") : "Lead"}
		</PropertyTrigger>
	)

	const priorityTrigger = (
		<PropertyTrigger
			tooltip="Change priority"
			icon={
				<IssuePriorityIcon
					priority={project.priority ?? "No priority"}
					className={propertyIconClassName}
				/>
			}
		>
			{project.priority ?? "No priority"}
		</PropertyTrigger>
	)

	const isViewer = role === "VIEWER"

	return (
		<>
			{isViewer ? (
				priorityTrigger
			) : (
				<PopoverButton
					data={priorityOptions}
					selected={
						priorities
							?.find(({ label }) => label === project.priority)
							?.priority.toString() ?? undefined
					}
					isLoading={isPriorityOpen && !priorities}
					onOpenChange={setIsPriorityOpen}
					placeholder="Change priority..."
					numberShortcuts
					onChange={(value) => {
						const option = priorities?.find(
							({ priority }) => String(priority) === value,
						)
						if (!option) return
						void save(
							{ priority: { value: option.priority, label: option.label } },
							"project-priority-update",
						)
					}}
				>
					{priorityTrigger}
				</PopoverButton>
			)}

			{isViewer ? (
				leadTrigger
			) : (
				<PopoverButton
					data={leadOptions}
					selected={project.leadId ?? undefined}
					isLoading={isLeadOpen && !members}
					onOpenChange={setIsLeadOpen}
					placeholder="Set lead..."
					resetOption="Remove lead"
					resetIcon={UserXmarkIcon}
					onChange={(leadId) => void save({ leadId }, "project-lead-update")}
				>
					{leadTrigger}
				</PopoverButton>
			)}

			{health && (
				<PropertyTrigger
					tooltip="Health from the latest project update in Linear"
					icon={
						<HealthIcon className={cn("h-4 w-4 flex-none", health.className)} />
					}
				>
					{health.label}
				</PropertyTrigger>
			)}

			{percent !== null && (
				<PropertyTrigger
					tooltip="Progress in Linear"
					icon={
						<CircularProgress
							percent={percent}
							className="h-3.5 w-3.5 flex-none"
							thickness={14}
						/>
					}
				>
					{percent}%
				</PropertyTrigger>
			)}
		</>
	)
}
